import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { SnackbarProvider } from 'notistack';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { PrivateRoute, AdminRoute } from './components/ProtectedRoute';
import SolicitudRolMedico from './components/SolicitudRolMedico';
import GestionSolicitudesRol from './components/admin/GestionSolicitudesRol';

// Componentes
import Layout from './components/Layout';
import Home from './components/Home';
import Login from './components/Login';
import Registro from './components/Registro';
import RecuperarPassword from './components/RecuperarPassword';
import DashboardInicio from './components/DashboardInicio';
import AdminPanel from './components/admin/PanelAdmin';
import ProtectedRoute from './components/ProtectedRoute';
import NuevaConsulta from './components/NuevaConsulta';
import AnalisisExamenes from './components/AnalisisExamenes';
import ListaCasos from './components/ListaCasos';
import Perfil from './components/Perfil';
import TerminosCondiciones from './components/TerminosCondiciones';
import PanelMedico from './components/medico/PanelMedico';
import RestablecerPassword from './components/RestablecerPassword';

// Crear tema personalizado
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#fff',
    },
    secondary: {
      main: '#9c27b0',
      light: '#ba68c8',
      dark: '#7b1fa2',
      contrastText: '#fff',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
      contrastText: '#fff',
    },
    warning: {
      main: '#ed6c02',
      light: '#ff9800',
      dark: '#e65100',
      contrastText: '#fff',
    },
    info: {
      main: '#0288d1',
      light: '#03a9f4',
      dark: '#01579b',
      contrastText: '#fff',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
      contrastText: '#fff',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      A100: '#f5f5f5',
      A200: '#eeeeee',
      A400: '#bdbdbd',
      A700: '#616161',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    background: {
      paper: '#fff',
      default: '#fafafa',
    },
    common: {
      black: '#000',
      white: '#fff',
    },
  },
});

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SnackbarProvider maxSnack={3} autoHideDuration={3000}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <Routes>
              {/* Rutas públicas */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/registro" element={<Registro />} />
              <Route path="/recuperar-password" element={<RecuperarPassword />} />
              <Route path="/reset-password/:token" element={<RestablecerPassword />} />
              <Route path="/terminos-condiciones" element={<Layout><TerminosCondiciones /></Layout>} />
              <Route path="/privacidad" element={<Layout><TerminosCondiciones privacyOnly={true} /></Layout>} />
              
              {/* Rutas protegidas */}
              <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardInicio /></Layout></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><Layout><AdminPanel /></Layout></ProtectedRoute>} />
              <Route path="/nueva-consulta" element={<ProtectedRoute><Layout><NuevaConsulta /></Layout></ProtectedRoute>} />
              <Route path="/analisis-examenes" element={<ProtectedRoute><Layout><AnalisisExamenes /></Layout></ProtectedRoute>} />
              <Route path="/casos" element={<ProtectedRoute><Layout><ListaCasos /></Layout></ProtectedRoute>} />
              <Route path="/perfil" element={<ProtectedRoute><Layout><Perfil /></Layout></ProtectedRoute>} />
              <Route 
                path="/panel-medico" 
                element={
                  <ProtectedRoute allowedRoles={['medico', 'admin']}>
                    <Layout>
                      <PanelMedico />
                    </Layout>
                  </ProtectedRoute>
                } 
              />

              {/* Ruta para solicitar rol médico */}
              <Route
                path="/solicitar-rol-medico"
                element={
                  <PrivateRoute allowedRoles={['paciente']}>
                    <Layout>
                      <SolicitudRolMedico />
                    </Layout>
                  </PrivateRoute>
                }
              />

              {/* Ruta para gestionar solicitudes (admin) */}
              <Route
                path="/admin/solicitudes-rol"
                element={
                  <AdminRoute>
                    <Layout>
                      <GestionSolicitudesRol />
                    </Layout>
                  </AdminRoute>
                }
              />

              {/* Ruta por defecto */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </LocalizationProvider>
        </SnackbarProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;